import { cva, type VariantProps } from 'class-variance-authority';
import * as SwitchPrimitives from '@radix-ui/react-switch';
import { cn } from '@/lib/utils';
import React from 'react';
import percentBlueBold from './percent_blue_bold.svg';
import percentWhiteRegular from './percent_white_regular.svg';
import dollarBlueBold from './dollar_blue_bold.svg';
import dollarWhiteRegular from './dollar_white_regular.svg';

const toggleVariants = cva(
  cn(
    'w-[32px]',
    'h-[16px]',
    'rounded-full',
    'bg-venn-white',
    'venn-text-venn-dark-blue',
    'box-sizing-content-box',
    'text-[10px]',
    'tracking-[1px]',
    'p-[1px]',
  ),
  {
    variants: {
      variant: {
        enabled: '',
        disabled: '[&&]:bg-venn-grey cursor-not-allowed pointer-events-none user-select-none [&&]:color-venn-black',
      },
    },
  },
);
type PercentageSwitchProps = VariantProps<typeof toggleVariants> & {
  className?: string;
  toggled: boolean;
  setToggled?: (toggled: boolean) => void;
};
export const PercentageSwitch = ({ variant, className, toggled, setToggled }: PercentageSwitchProps) => {
  const innerImageSrc = (() => {
    if (variant === 'disabled') {
      return toggled ? percentWhiteRegular : dollarWhiteRegular;
    }
    return toggled ? percentBlueBold : dollarBlueBold;
  })();
  return (
    <SwitchPrimitives.Root
      className={cn(toggleVariants({ variant }), 'relative', 'bg-venn-dark-blue', className)}
      checked={toggled}
      onCheckedChange={setToggled}
    >
      <SwitchPrimitives.Thumb
        className={cn(
          'flex',
          'justify-center',
          'items-center',
          'h-[12px]',
          'w-[12px]',
          'rounded-full',
          variant === 'disabled' ? 'bg-venn-grey-90/20' : 'bg-venn-white',
          'absolute',
          'data-[state=checked]:translate-x-[17px]',
          'data-[state=unchecked]:translate-x-[1px]',
          'transition-transform',
          'top-[2px]',
        )}
      >
        <img src={innerImageSrc} alt={toggled ? '%' : '$'} />
      </SwitchPrimitives.Thumb>
      <div
        className={cn(
          'flex',
          'h-[12px]',
          'w-[12px]',
          'justify-center',
          'items-center',
          'absolute',
          'top-[2px]',
          toggled ? 'left-[2px]' : 'right-[2px]',
        )}
      >
        <img src={toggled ? dollarWhiteRegular : percentWhiteRegular} alt={toggled ? '$' : '%'} />
      </div>
    </SwitchPrimitives.Root>
  );
};
